import { StatusType } from '@models/landing/ui/select-passenger-model';
import type { CheckedInPax, CheckedInPaxLeg } from '@models/ui/pnr-data-model';

export type LeftBorderColor = 'green' | 'yellow' | 'red' | 'default';

/** Exact API status strings from Angular OLCI. */
export const PaxStatus = {
  CHECKED_IN: 'CheckedIn',
  CHECK_IN_OPEN: 'CheckInOpen',
  STAND_BY: 'StandBy',
  STAND_BY_ID90: 'StandById90',
  STANDBY_WAITLISTED: 'StandbyWaitListed',
  NOT_CHECKED_IN: 'NotCheckedIn',
} as const;

export type PaxStatusValue = (typeof PaxStatus)[keyof typeof PaxStatus];

const STAND_BY_STATUSES = new Set<string>([
  PaxStatus.STAND_BY,
  PaxStatus.STAND_BY_ID90,
  PaxStatus.STANDBY_WAITLISTED,
]);

export const isCheckedInStatus = (status: string | null | undefined): boolean =>
  String(status) === PaxStatus.CHECKED_IN || String(status) === String(StatusType.CHECKED_IN);

export const isStandById90Status = (status: string | null | undefined): boolean =>
  String(status).toLowerCase() === PaxStatus.STAND_BY_ID90.toLowerCase();

export const isStandByWaitlistStatus = (status: string | null | undefined): boolean =>
  String(status).toLowerCase() === PaxStatus.STANDBY_WAITLISTED.toLowerCase();

export const isStandByStatus = (status: string | null | undefined): boolean => {
  const value = String(status);
  return (
    STAND_BY_STATUSES.has(value) || isStandById90Status(value) || isStandByWaitlistStatus(value)
  );
};

export const countCheckedInFlights = (statuses: (string | null | undefined)[]): number =>
  statuses.filter((status) => isCheckedInStatus(status)).length;

export const countStandByFlights = (statuses: (string | null | undefined)[]): number =>
  statuses.filter((status) => isStandByStatus(status)).length;

export const countAllowCheckIn = (allowedFlags: (boolean | null | undefined)[]): number =>
  allowedFlags.filter((allowed) => allowed === true).length;

/** Angular: isAllowCheckInCount === legs.length */
export const canSelectForCheckIn = (isAllowCheckInCount: number, legsLength: number): boolean =>
  legsLength > 0 && isAllowCheckInCount === legsLength;

export const hasStandBy = (standByFlightsCount: number): boolean => standByFlightsCount > 0;

export const resolveStandByID90 = (statuses: (string | null | undefined)[]): number =>
  statuses.some((status) => isStandById90Status(status)) ? 1 : 0;

export const resolveStandByWaitlist = (statuses: (string | null | undefined)[]): boolean =>
  statuses.some((status) => isStandByWaitlistStatus(status));

/**
 * Angular left-border rules:
 * - Green: all CheckedIn, or all StandBy, or CheckedIn+StandBy cover every leg
 * - Yellow: cannot select and checkedInFlightsCount > 0
 * - Red: cannot select and checkedInFlightsCount === 0
 * - Default: selectable (full allow-check-in)
 */
export const resolveLeftBorderColor = (params: {
  isAllowCheckInCount: number;
  legsLength: number;
  checkedInFlightsCount: number;
  standByFlightsCount: number;
}): LeftBorderColor => {
  const canSelect = canSelectForCheckIn(params.isAllowCheckInCount, params.legsLength);
  const { legsLength, checkedInFlightsCount, standByFlightsCount } = params;

  if (legsLength > 0) {
    const allCheckedIn = checkedInFlightsCount === legsLength;
    const allStandBy = standByFlightsCount === legsLength;
    const coveredByCiOrStandBy =
      checkedInFlightsCount + standByFlightsCount > 0 &&
      checkedInFlightsCount + standByFlightsCount >= legsLength;
    if (allCheckedIn || allStandBy || coveredByCiOrStandBy) {
      return 'green';
    }
  }

  if (!canSelect && checkedInFlightsCount > 0) {
    return 'yellow';
  }

  if (!canSelect && checkedInFlightsCount === 0) {
    return 'red';
  }

  return 'default';
};

export const RANDOM_ASSIGNED_SEAT = 'RANDOM_ASSIGNED' as const;

/** Angular: CheckedIn + SEAT extra present + allowedSeat. */
export const canShowChangeSeat = (leg: CheckedInPaxLeg): boolean =>
  isCheckedInStatus(leg.status) &&
  Boolean(leg.hasSeatExtra) &&
  leg.paxRestrictions?.allowedSeat === true;

/** Angular: RANDOM_ASSIGNED && !seatInclusiveFare → popup, no navigate. */
export const isRandomAssignedSeatChangeBlocked = (leg: CheckedInPaxLeg): boolean =>
  leg.seatAssignmentType === RANDOM_ASSIGNED_SEAT && !leg.seatInclusiveFare;

export const findCheckedInLeg = (
  checkedInPaxList: CheckedInPax[],
  paxId: string,
  physicalFlightId: string,
): CheckedInPaxLeg | undefined => {
  const passenger = checkedInPaxList.find((pax) => pax.paxId === paxId);
  return passenger?.legs.find((leg) => leg.physicalFlightID === physicalFlightId);
};

export const resolveChangeSeatPrimaryLastName = (checkedInPaxList: CheckedInPax[]): string =>
  (checkedInPaxList[0]?.lastName ?? '').trim();
